import { useState } from "react";
import { Info } from "lucide-react";

type Mode = "port" | "twilio";

interface PortParams {
  mode: "port";
  phoneNumber: string;
  friendlyName: string;
  countryCode: string;
  businessName: string;
  authorizedRepresentative: string;
  contactEmail: string;
  accountNumber?: string;
  currentCarrier?: string;
  billingAddress?: string;
  authorizationDocumentUrl?: string;
  latestBillUrl?: string;
  notes?: string;
}

interface TwilioAttachParams {
  mode: "twilio";
  phoneNumber: string;
  friendlyName?: string;
  contactEmail: string;
}

interface ExistingNumberFormProps {
  genieName: string;
  brand?: string;
  onSubmit: (params: PortParams | TwilioAttachParams) => Promise<void>;
  onCancel: () => void;
  isSubmitting: boolean;
}

const inputClass = "w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent";

const ExistingNumberForm = ({ genieName, brand = "#4E9CFF", onSubmit, onCancel, isSubmitting }: ExistingNumberFormProps) => {
  const [mode, setMode] = useState<Mode>("port");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [friendlyName, setFriendlyName] = useState("");

  // Port-only fields
  const [countryCode, setCountryCode] = useState("US");
  const [businessName, setBusinessName] = useState("");
  const [authorizedRepresentative, setAuthorizedRepresentative] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [currentCarrier, setCurrentCarrier] = useState("");
  const [billingAddress, setBillingAddress] = useState("");
  const [authorizationDocumentUrl, setAuthorizationDocumentUrl] = useState("");
  const [latestBillUrl, setLatestBillUrl] = useState("");
  const [notes, setNotes] = useState("");

  const ringStyle = { "--tw-ring-color": `${brand}80` } as React.CSSProperties;
  const isPhoneValid = phoneNumber.replace(/\D/g, "").length >= 10;
  const canSubmit =
    isPhoneValid &&
    (mode === "twilio"
      ? contactEmail.trim().length > 0
      : businessName.trim() && authorizedRepresentative.trim() && contactEmail.trim());

  const reset = () => {
    setPhoneNumber("");
    setFriendlyName("");
    setCountryCode("US");
    setBusinessName("");
    setAuthorizedRepresentative("");
    setContactEmail("");
    setAccountNumber("");
    setCurrentCarrier("");
    setBillingAddress("");
    setAuthorizationDocumentUrl("");
    setLatestBillUrl("");
    setNotes("");
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;

    if (mode === "twilio") {
      await onSubmit({ mode: "twilio", phoneNumber, friendlyName: friendlyName || undefined, contactEmail });
    } else {
      await onSubmit({
        mode: "port",
        phoneNumber,
        friendlyName: friendlyName || `${genieName} Existing Number`,
        countryCode,
        businessName,
        authorizedRepresentative,
        contactEmail,
        accountNumber,
        currentCarrier,
        billingAddress,
        authorizationDocumentUrl,
        latestBillUrl,
        notes,
      });
    }

    reset();
  };

  const handleCancel = () => {
    reset();
    onCancel();
  };

  const switchMode = (next: Mode) => {
    reset();
    setMode(next);
  };

  return (
    <div className="border-t border-gray-200 pt-6">
      <h3 className="text-lg font-medium text-gray-900 mb-1">Move your number to us permanently</h3>
      <p className="text-sm text-gray-500 mb-4">
        The slower, permanent option. Forwarding gets the same calls answered today.
      </p>

      {/* Mode toggle */}
      <div className="flex gap-2 mb-5">
        <button
          type="button"
          onClick={() => switchMode("port")}
          className="flex-1 px-4 py-2.5 rounded-lg text-sm font-medium border-2 transition-colors text-left"
          style={
            mode === "port"
              ? { borderColor: brand, backgroundColor: `${brand}10`, color: brand }
              : { borderColor: "#E5E7EB", color: "#6B7280" }
          }
        >
          <span className="block font-semibold">Move it from your carrier</span>
          <span className="text-xs opacity-75">Up to five business days, and our team works it with you</span>
        </button>
        <button
          type="button"
          onClick={() => switchMode("twilio")}
          className="flex-1 px-4 py-2.5 rounded-lg text-sm font-medium border-2 transition-colors text-left"
          style={
            mode === "twilio"
              ? { borderColor: brand, backgroundColor: `${brand}10`, color: brand }
              : { borderColor: "#E5E7EB", color: "#6B7280" }
          }
        >
          <span className="block font-semibold">You host this number yourself</span>
          <span className="text-xs opacity-75">You manage it in your own calling account already</span>
        </button>
      </div>

      <div className="space-y-4">
        {/* Info banner */}
        <div className="rounded-lg p-3" style={{ backgroundColor: `${brand}10`, border: `1px solid ${brand}30` }}>
          <div className="flex gap-2">
            <Info className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: brand }} />
            <p className="text-xs" style={{ color: brand }}>
              {mode === "port"
                ? "This starts a number transfer. It takes up to five business days, our team works it with you, and calls will not reach this genie until it completes. If you need calls answered today, forward your number instead."
                : "This is not automatic. We will email you to arrange moving the number across, which takes a few days."}
            </p>
          </div>
        </div>

        {/* Phone number — always shown */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              className={inputClass}
              style={ringStyle}
              placeholder="+1 (555) 123-4567"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              disabled={isSubmitting}
            />
          </div>

          {mode === "port" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
              <select
                className={inputClass}
                style={ringStyle}
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                disabled={isSubmitting}
              >
                <option value="US">United States (+1)</option>
                <option value="AU">Australia (+61)</option>
                <option value="NZ">New Zealand (+64)</option>
              </select>
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Friendly Name</label>
          <input
            type="text"
            className={inputClass}
            style={ringStyle}
            placeholder={`${genieName} Number`}
            value={friendlyName}
            onChange={(e) => setFriendlyName(e.target.value)}
            disabled={isSubmitting}
          />
        </div>

        {/* Contact email — always shown (required for both modes) */}
        {mode === "twilio" && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Contact Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              className={inputClass}
              style={ringStyle}
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              disabled={isSubmitting}
            />
          </div>
        )}

        {/* Port-only fields */}
        {mode === "port" && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Business Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className={inputClass}
                  style={ringStyle}
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Authorized Representative <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className={inputClass}
                  style={ringStyle}
                  value={authorizedRepresentative}
                  onChange={(e) => setAuthorizedRepresentative(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contact Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                className={inputClass}
                style={ringStyle}
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                disabled={isSubmitting}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Account Number</label>
                <input
                  type="text"
                  className={inputClass}
                  style={ringStyle}
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Current Carrier</label>
                <input
                  type="text"
                  className={inputClass}
                  style={ringStyle}
                  value={currentCarrier}
                  onChange={(e) => setCurrentCarrier(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Billing Address</label>
              <textarea
                className={inputClass}
                style={ringStyle}
                rows={2}
                value={billingAddress}
                onChange={(e) => setBillingAddress(e.target.value)}
                disabled={isSubmitting}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Authorization Document Link</label>
                <input
                  type="url"
                  className={inputClass}
                  style={ringStyle}
                  value={authorizationDocumentUrl}
                  onChange={(e) => setAuthorizationDocumentUrl(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Latest Bill Link</label>
                <input
                  type="url"
                  className={inputClass}
                  style={ringStyle}
                  value={latestBillUrl}
                  onChange={(e) => setLatestBillUrl(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
              <textarea
                className={inputClass}
                style={ringStyle}
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
          </>
        )}
      </div>

      <div className="flex justify-end gap-3 mt-6">
        <button
          type="button"
          onClick={handleCancel}
          className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit || isSubmitting}
          className="px-4 py-2 text-white rounded-lg transition-opacity disabled:opacity-50 font-medium"
          style={{ backgroundColor: brand }}
          onMouseEnter={(e) => !isSubmitting && canSubmit && (e.currentTarget.style.opacity = "0.85")}
          onMouseLeave={(e) => !isSubmitting && canSubmit && (e.currentTarget.style.opacity = "1")}
        >
          {isSubmitting
            ? mode === "twilio" ? "Attaching..." : "Submitting..."
            : mode === "twilio" ? "Attach Number" : "Submit Request"}
        </button>
      </div>
    </div>
  );
};

export default ExistingNumberForm;
